import { Engine, RuleProperties, RuleResult } from "json-rules-engine";
import { IRuleFailure } from "../journeys/types/journeyUxSchema.types";
import { registerAnnualReviewFacts } from "../journeys/services/annualReviewFacts";

export interface IConditionEvalResult {
  passed: boolean;
  failures: IRuleFailure[];
  passedFacts: IRuleFailure[];
}

export type TRuleValue = string | number | boolean | null | undefined | TRuleObject | TRuleArray;

export interface TRuleObject {
  [key: string]: TRuleValue;
}

export interface TRuleArray extends Array<TRuleValue> {}

export type TRuleFacts = Record<string, TRuleValue>;

interface IMilestoneCompletedParams {
  id?: string | number;
}

interface ILatestMilestoneIdParams {
  ids?: (string | number)[];
}

interface IRuleFailureNode {
  all?: IRuleFailureNode[];
  any?: IRuleFailureNode[];
  not?: IRuleFailureNode;
  fact?: string;
  path?: string;
  operator?: string;
  value?: TRuleValue;
  justification?: string;
  result?: boolean;
  factResult?: TRuleValue;
}

export function createEngine(): Engine {
  const engine = new Engine([], { allowUndefinedFacts: true });

  engine.addOperator(
    "doesNotStartWithAny",
    (factValue: unknown, jsonValue: unknown) => {
      if (typeof factValue !== "string" || !Array.isArray(jsonValue)) return true;
      return !jsonValue.some((prefix) => typeof prefix === "string" && factValue.startsWith(prefix));
    }
  );

  engine.addFact(
    "milestoneCompleted",
    async (params: IMilestoneCompletedParams = {}, almanac) => {
      const milestones = (await almanac.factValue("milestones")) as
        | Record<string, { completed?: boolean }>
        | undefined
        | null;

      const rawId = params.id;
      if (rawId == null) {
        return false;
      }

      const milestoneId =
        typeof rawId === "number" && Number.isFinite(rawId)
          ? String(rawId)
          : typeof rawId === "string"
            ? rawId.trim()
            : "";

      if (!milestoneId) {
        return false;
      }

      return milestones?.[milestoneId]?.completed === true;
    },
    { cache: false }
  );

  engine.addFact(
    "latestMilestoneId",
    async (params: ILatestMilestoneIdParams = {}, almanac) => {
      const milestones = (await almanac.factValue("milestones")) as
        | Record<string, { completed?: boolean; completedDate?: string }>
        | undefined
        | null;

      const ids = Array.isArray(params.ids) ? params.ids : [];

      let latestId: string | null = null;
      let latestTime = -Infinity;

      for (const rawId of ids) {
        const milestoneId = String(rawId).trim();
        const milestone = milestones?.[milestoneId];
        if (!milestone?.completed || !milestone.completedDate) continue;

        const time = new Date(milestone.completedDate).getTime();
        if (Number.isNaN(time) || time < latestTime) continue;

        latestTime = time;
        latestId = milestoneId;
      }

      return latestId;
    },
    { cache: false }
  );

  registerAnnualReviewFacts(engine);

  return engine;
}

export async function evaluateConditions(
  conditions: RuleProperties["conditions"],
  facts: TRuleFacts
): Promise<IConditionEvalResult> {
  const engine = createEngine();
  engine.addRule({ conditions, event: { type: "match" } });

  for (const key of Object.keys(facts)) {
    engine.addFact(key, facts[key], { cache: false });
  }

  const { results, failureResults } = await engine.run({});
  const passed = results.length > 0;

  const matchedFacts: IRuleFailure[] = results.flatMap((r: RuleResult) =>
    extractFacts(r.conditions as IRuleFailureNode, true, undefined)
  );
  const failedFacts: IRuleFailure[] = passed
    ? []
    : failureResults.flatMap((r: RuleResult) =>
        extractFacts(r.conditions as IRuleFailureNode, false, undefined)
      );

  return {
    passed,
    failures: failedFacts,
    passedFacts: matchedFacts,
  };
}

function extractFacts(
  node: IRuleFailureNode | null | undefined,
  expectedResult: boolean,
  inheritedJustification?: string
): IRuleFailure[] {
  if (!node) return [];

  const nextJustification = node.justification ?? inheritedJustification;

  if (Array.isArray(node.all)) {
    return node.all.flatMap((child) =>
      extractFacts(child, expectedResult, nextJustification)
    );
  }

  if (Array.isArray(node.any)) {
    return node.any.flatMap((child) =>
      extractFacts(child, expectedResult, nextJustification)
    );
  }

  if (node.not) {
    return extractFacts(node.not, expectedResult, inheritedJustification);
  }

  if (node.fact && node.result === expectedResult) {
    return [
      {
        fact: node.fact,
        path: node.path,
        operator: node.operator ?? "unknown",
        value: node.value,
        factResult: node.factResult,
        justification: node.justification ?? inheritedJustification,
      },
    ];
  }

  return [];
}
